import { useState } from 'react';
import { Box, IconButton, Stack, Typography, Button, Grid2 } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import CustomDivider from '../../shared/utils/CustomDivider';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import ToggleSwitch from '../../shared/utils/CustomToggle';
import { postLmsGrades } from '../../assets/nodeApi/postApi/postAdminApi';
import { putLmsGrades } from '../../assets/nodeApi/putApi/putAdminApi';
import { patchLmsGrades } from '../../assets/nodeApi/patchApi/PatchAdminApi';
import { Grid_Gap, Input_Width } from '../../shared/constants';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { queryClient } from '../../assets/nodeApi/reactQueryClient';
import { useLmsDropdownOptions } from './option/LsmOption';

const LsmGrade = () => {
    const { showToaster } = useToaster();
    const [selectedGrade, setSelectedGrade] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const { lmsGrade } = useLmsDropdownOptions()
    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Name is required'),
        numeric_value: Yup.number().required('Numeric value is required'),
        description: Yup.string().required('Description is required'),
        port: Yup.number().required('Port is required'),
    });

    const initialValues = {
        name: selectedGrade?.name || '',
        numeric_value: selectedGrade?.numeric_value || '',
        description: selectedGrade?.description || '',
        port: selectedGrade?.port || ''
    };

    const mutationAdd = useMutation({
        mutationFn: (formData) => postLmsGrades(formData),
        onSuccess: () => {
            showToaster('Grade added successfully!', '', 'success');
            queryClient.invalidateQueries(['lms/grades']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to add grade', '', 'error');
        },
    });

    // Update grade
    const mutationUpdate = useMutation({
        mutationFn: ({ formData, id }) => putLmsGrades(formData, id),
        onSuccess: () => {
            showToaster('Grade updated successfully!', '', 'success');
            queryClient.invalidateQueries(['lms/grades']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update grade', '', 'error');
        },
    });

    const handleSubmit = (values, { resetForm }) => {

        const payload = {
            ...values,
            numeric_value: Number(values.numeric_value),
        };
        if (isEditing) {
            mutationUpdate.mutate({ formData: payload, id: selectedGrade.id, });
        } else {
            mutationAdd.mutate(payload);
            resetForm();
        }
        setIsEditing(false);
        setSelectedGrade(null);
    };

    const toggleStatusMutation = useMutation({
        mutationFn: (formData, id) => patchLmsGrades(formData, id),
        onSuccess: (response) => {
            showToaster(`Grade ${response?.data?.status === 1 ? "Enabled" : "Disabled"} successfully!`, '', 'success');
            queryClient.invalidateQueries(['lms/grades']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update status', '', 'error');
        },
    });



    const gradeColumns = [
        { field: 'sr_no', headerName: 'Sr No', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'name', headerName: 'Name', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'numeric_value', headerName: 'Numeric Value', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'description', headerName: 'Description', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'port', headerName: 'Port No', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        {
            field: 'actionsEdit',
            headerName: 'Edit',
            flex: 1,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <IconButton
                        onClick={() => {
                            setSelectedGrade(params.row);
                            setIsEditing(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        size="small"
                    >
                        <EditIcon />
                    </IconButton>
                </Stack>
            ),
        },
        {
            field: 'actionsToggle',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <ToggleSwitch checked={params.row.status === 1 ? true : false} onCheck={() => toggleStatusMutation.mutate(params?.row?.id)} />
            ),
        },
    ];

    return (
        <Box>
            <CustomDivider />
            <NormalContainerWithHeader headerText={isEditing ? 'Edit Grade' : 'Add Grade'}>
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                    {({ resetForm }) => (
                        <Form>
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 children size={Input_Width}>
                                    <Field name="name" component={TextFormField} label="Name" isRequired />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="numeric_value"
                                        component={TextFormField}
                                        label="Numeric Value" isRequired
                                        type={"number"}
                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field name="description" component={TextFormField} label="Description" isRequired />
                                </Grid2>

                                <Grid2 children size={Input_Width}>
                                    <Field name="port" component={TextFormField} label="Port" type={"number"} isRequired />
                                </Grid2>
                            </Grid2>
                            <CustomDivider />
                            <Grid2 container spacing={Grid_Gap} sx={{justifyContent:"end"}}>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        onClick={() => {
                                            resetForm();
                                            setIsEditing(false);
                                            setSelectedGrade(null);
                                        }}
                                        fullWidth
                                    >
                                        {isEditing ? 'Cancel' : 'Reset'}
                                    </Button>
                                </Grid2>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        disabled={mutationAdd.isPending || mutationUpdate.isPending}
                                    >
                                        {mutationUpdate.isPending
                                            ? "Updating..."
                                            : mutationAdd.isPending
                                                ? "Submitting..."
                                                : isEditing
                                                    ? "Update"
                                                    : "Submit"}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>

            <CustomDivider />

            <TableFilterTemplate
                title="Grade List"
                columns={gradeColumns}
                rowsData={lmsGrade.options || []}
                isLoading={lmsGrade.isLoading} />
        </Box>
    );
};

export default LsmGrade;
